import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { FaRegUser, FaRegEdit, FaCamera } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import toast from "react-hot-toast";
import { userUpdateURL } from '../api/api';
import { useAuthContext } from '../context';
import UploadUserImages from "../components/Modal/UploadUserImages";
import UploadProfilePicture from "../components/Modal/UploadProfilePicture";
import { Country, State, City } from 'country-state-city';
import { Helmet } from "react-helmet";

export default function Profile() {
  const navigate = useNavigate();
  const { OTPset, user, fetchUser, token } = useAuthContext();

  // Loading only if we don't yet have a user
  const [loading, setLoading] = useState(!user?.user_id);
  const [editMode, setEditMode] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showProfileUpload, setShowProfileUpload] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const scrollToTop = () =>
    window.scrollTo({ top: 0, behavior: "smooth" });

  // form data
  const [data, setData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    age: '',
    country: '',
    state: '',
    city: '',
    gender: '',
    interestedIn: '',
    description: '',
  });

  // Country/state/city lists
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Lightbox
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const openLightbox = (url) => {
    setModalImage(url);
    setShowImageModal(true);
  };

  // parse user.image into an array, once per change
  const parsedImages = useMemo(() => {
    if (!user?.image) return [];
    let imgs = user.image;
    if (typeof imgs === 'string') {
      try { imgs = JSON.parse(imgs); } catch {}
    }
    if (typeof imgs === 'string') {
      try { imgs = JSON.parse(imgs); } catch {}
    }
    return Array.isArray(imgs) ? imgs : [];
  }, [user?.image]);

  // only fetch on mount if we don't already have a loaded user
  useEffect(() => {
    if (!user?.user_id) {
      fetchUser()
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [user?.user_id, fetchUser]);

  // load country list once
  useEffect(() => {
    setCountries(Country.getAllCountries() || []);
  }, []);

  // populate form when user arrives (and we're not editing)
  useEffect(() => {
    if (!user?.user_id || editMode) return;
    setData({
      firstName:    user.firstName    || '',
      lastName:     user.lastName     || '',
      email:        user.email        || '',
      mobile:       user.mobile       || '',
      age:          user.age          || '',
      country:      user.country      || '',
      state:        user.state        || '',
      city:         user.city         || '',
      gender:       user.gender       || '',
      interestedIn: user.interestedIn || '',
      description:  user.description  || '',
    });
    if (user.country) {
      setStates(State.getStatesOfCountry(user.country) || []);
    }
    if (user.country && user.state) {
      setCities(City.getCitiesOfState(user.country, user.state) || []);
    }
  }, [user, editMode]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'country') {
      setData(d => ({ ...d, country: value, state: '', city: '' }));
      setStates(State.getStatesOfCountry(value) || []);
      setCities([]);
    } else if (name === 'state') {
      setData(d => ({ ...d, state: value, city: '' }));
      setCities(City.getCitiesOfState(data.country, value) || []);
    } else {
      setData(d => ({ ...d, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const phoneRegex = /^\+?\d{10,15}$/;
    if (!phoneRegex.test(data.mobile)) {
      alert("Please enter a valid phone number (10–15 digits).");
      return;
    }

    if (data.age && (data.age < 18 || data.age > 100)) {
      return toast.error("Age must be between 18 and 100");
    }
    setReloading(true);
    try {
      const { data: res } = await axios.patch(
        userUpdateURL,
        { ...data, id: user.user_id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Updated Successfully");
      setEditMode(false);
      // update localStorage & then re-fetch so context stays fresh
      localStorage.setItem('userData', JSON.stringify(res.user));
      await fetchUser();
    } catch (err) {
      toast.error(`Error: ${err.message}`);
    } finally {
      setReloading(false);
    }
  };
  
  const handleEmail = async () => {
    const email = user?.email || '';
    if (!email) {
      toast.error("Email is required to proceed.");
      return;
    }
  
    setEmailLoading(true);
    try {
      if (!user?.isEmailVerified) {
        const result = await OTPset({ email });
        if (result) {
          toast.success("OTP sent to email");
          navigate('/verify-otp', { state: { email, purpose: 'emailVerification', fromProfile: true } });
          return;
        }
      } else {
        navigate("/profile");
        toast.success("Email Verification Successful");
      }
    } catch (error) {
      toast.error("Failed to send OTP");
    } finally {
      setEmailLoading(false);
    }
  };
  

  const handleOTP = async () => {
    const email = user?.email;
    if (!email) return;
  
    setOtpLoading(true);
    try {
      const result = await OTPset({ email });
      if (result) {
        toast.success("OTP sent to email");
        navigate('/verify-otp', { state: { purpose: 'passwordChange' } });
      }
    } catch (error) {
      console.error("Error in handleOTP:", error);
      toast.error("An error occurred while sending OTP");
    } finally {
      setOtpLoading(false);
    }
  };
  

  const handleUploadSuccess = async () => {
    await fetchUser();
    setShowUploadModal(false);
  };
  const handleProfileUploadSuccess = async () => {
    await fetchUser();
    setShowProfileUpload(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-red-400" />
      </div>
    );
  }

  const isGold = user.membership_type === "gold" && user.membership_status;

  const incompleteFields = [];

[
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "age", label: "Age" },
  { key: "gender", label: "Gender" },
  { key: "interestedIn", label: "Interested In" },
  { key: "country", label: "Country" },
  { key: "state", label: "State" },
  { key: "city", label: "City" },
  { key: "description", label: "About Me" },
].forEach(({ key, label }) => {
  if (!user?.[key]) {
    incompleteFields.push(label);
  }
});

  return (
    <>
    <Helmet>
      <title>Your Profile – SomeOne4u</title>
      <meta
        name="description"
        content="Manage your SomeOne4u profile: update your info, upload photos, and stay connected."
      />
      <meta
        name="keywords"
        content="SomeOne4u, profile, edit profile, user settings"
      />
      <link rel="canonical" href="https://someone4u.com/profile" />

      <meta property="og:title" content="Your Profile – SomeOne4u" />
      <meta
        property="og:description"
        content="Manage your SomeOne4u profile: update your info, upload photos, and stay connected."
      />
      <meta property="og:url" content="https://someone4u.com/profile" />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="SomeOne4u" />
    </Helmet>

    <div className="min-h-screen bg-gradient-to-br from-blue-100 via-pink-100 to-white py-10 px-4 sm:px-6 lg:px-8">
  <div className="max-w-6xl mx-auto bg-white shadow-xl rounded-2xl overflow-hidden border-t-4 border-red-400">
    <div className="flex flex-col lg:flex-row">
      {/* Sidebar */}
      <div className="lg:w-1/3 p-8 bg-gray-50 border-r border-gray-200 text-center relative">
        <div className="relative w-fit mx-auto mb-4">
          <img
            onClick={() => openLightbox(user.profile_image || "/image/operator.png")}
            src={user.profile_image || "/image/operator.png"}
            alt="Profile"
            className="w-32 h-32 rounded-full object-cover shadow-lg border-4 border-white cursor-pointer"
          />
          <button
            onClick={() => setShowProfileUpload(true)}
            className="absolute bottom-0 right-0 bg-red-500 text-white p-2 rounded-full shadow hover:bg-red-600 transition"
          >
            <FaCamera />
          </button>
        </div>

        <h2 className="text-xl font-semibold text-gray-800">
          {user?.firstName} {user?.lastName}
        </h2>

        <span className={`mt-2 inline-block text-xs font-medium px-4 py-1 rounded-full ${
          isGold ? "bg-yellow-500 text-black" : "bg-green-600 text-white"
        }`}>
          {isGold ? "Gold Member" : "Member"}
        </span>

        {/* Reset / Verify buttons */}
        {user?.isEmailVerified && user?.password && (
          <div className="mt-4">
            <button
              onClick={handleOTP}
              disabled={otpLoading}
              className={`w-[130px] bg-slate-700 hover:bg-slate-800 text-white py-1.5 px-4 rounded-full text-sm font-medium transition ${
                otpLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {otpLoading ? 'Sending OTP...' : 'Reset Password'}
            </button>
          </div>
        )}

        {!user?.isEmailVerified && (
          <div className="mt-4 flex justify-center gap-2 flex-wrap">
            <button
              onClick={handleEmail}
              disabled={emailLoading}
              className={`bg-blue-600 hover:bg-blue-700 text-white py-1.5 px-4 rounded-full text-sm font-medium transition ${
                emailLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {emailLoading ? 'Sending OTP...' : 'Verify Email'}
            </button>

            <button
              onClick={handleOTP}
              disabled={otpLoading}
              className={`bg-slate-700 hover:bg-slate-800 text-white py-1.5 px-4 rounded-full text-sm font-medium transition ${
                otpLoading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {otpLoading ? 'Sending OTP...' : 'Reset Password'}
            </button>
          </div>
        )}

        {!isGold && (
          <div className="mt-8 bg-yellow-50 border border-yellow-400 rounded-lg p-4">
            <h4 className="text-yellow-800 font-semibold mb-2">Upgrade to Gold</h4>
            <p className="text-sm text-gray-600 mb-3">
              Unlock premium features like full image viewing and unlimited messaging.
            </p>
            <button
              onClick={() => {
                navigate("/membership");
                scrollToTop();
              }}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-full text-sm font-semibold transition"
            >
              Get Gold Membership
            </button>
          </div>
        )}
      </div>

      {/* Main Content */}
      <div className="lg:w-2/3 p-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-red-500">Your Profile</h2>
          <button
            onClick={() => setEditMode(e => !e)}
            className="flex items-center text-blue-600 text-sm gap-2 hover:underline"
          >
            <FaRegEdit /> Edit/Update
          </button>
        </div>

        {editMode ? (
          <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <InputField name="firstName" label="First Name" value={data.firstName} onChange={handleChange} />
            <InputField name="lastName" label="Last Name" value={data.lastName} onChange={handleChange} />
            <InputField name="email" label="Email" type="email" disabled value={data.email} onChange={handleChange} />
            <InputField name="mobile" label="Contact No." value={data.mobile} disabled={!!user.mobile} onChange={handleChange} />
            <InputField name="age" label="Age" type="number" value={data.age} onChange={handleChange} />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                name="gender"
                value={data.gender}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Interested In</label>
              <select
                name="interestedIn"
                value={data.interestedIn}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>

            <InputField name="description" label="About Me" value={data.description} onChange={handleChange} />

            {/* Country, State, City */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Country</label>
              <select
                name="country"
                value={data.country}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select Country</option>
                {countries.map(c => (
                  <option key={c.isoCode} value={c.isoCode}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">State</label>
              <select
                name="state"
                value={data.state}
                onChange={handleChange}
                disabled={!data.country}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select State</option>
                {states.map(s => (
                  <option key={s.isoCode} value={s.isoCode}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">City</label>
              <select
                name="city"
                value={data.city}
                onChange={handleChange}
                disabled={!data.state}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="">Select City</option>
                {cities.map((c, idx) => (
                  <option key={`${data.country}-${data.state}-${c.name}-${idx}`} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-span-2 text-right mt-2">
              <button
                type="submit"
                disabled={reloading}
                className={`px-6 py-2 rounded text-white ${
                  reloading ? 'bg-blue-400 cursor-not-allowed opacity-60' : 'bg-blue-600 hover:bg-blue-700'
                }`}
              >
                {reloading ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <DisplayField label="First Name" value={user?.firstName} />
            <DisplayField label="Last Name" value={user?.lastName || "Not provided"} />
            <DisplayField label="Email" value={user?.email} />
            <DisplayField label="Contact No." value={user?.mobile || "Not provided"} />
            <DisplayField label="Age" value={user?.age ? `${user.age} years` : "Not provided"} />
            <DisplayField label="Gender" value={user?.gender || "Not provided"} />
            <DisplayField label="Interested In" value={user?.interestedIn || "Not provided"} />
            <DisplayField label="Country" value={user?.country || "Not provided"} />
            <DisplayField label="State" value={user?.state || "Not provided"} />
            <DisplayField label="City" value={user?.city || "Not provided"} />
            <DisplayField label="About Me" value={user?.description || "No description yet"} />
          </div>
        )}

        {/* Incomplete fields warning */}
        {incompleteFields.length > 0 && !editMode && (
          <div className="mt-6 bg-indigo-50 border-l-4 border-indigo-500 p-4 rounded-md">
            <p className="text-sm text-indigo-700">
              Completing fields like age, gender, and location helps improve your chances of finding a match.
              <br />
              Please update: <strong>{incompleteFields.join(", ")}</strong>
            </p>
            <button
              onClick={() => setEditMode(true)}
              className="mt-3 inline-block bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-1.5 rounded-full text-sm transition"
            >
              Update Now
            </button>
          </div>
        )}

        {/* Uploaded Images */}
        <div className="mt-10">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700">Uploaded Images</h3>
            <button
              onClick={() => setShowUploadModal(true)}
              className="text-blue-500 text-sm hover:underline"
            >
              Manage/Upload Images
            </button>
          </div>

          {parsedImages.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {parsedImages.map((url, idx) => (
                <img
                  key={url}
                  src={url}
                  alt={`Upload ${idx + 1}`}
                  className="w-full h-32 object-cover rounded-lg cursor-pointer border border-gray-200"
                  onClick={() => openLightbox(url)}
                />
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-sm">No images uploaded yet.</p>
          )}
        </div>
      </div>
    </div>
  </div>

  {/* Modals */}
  {showUploadModal && (
    <UploadUserImages onClose={() => setShowUploadModal(false)} onUploadSuccess={handleUploadSuccess} />
  )}
  {showProfileUpload && (
    <UploadProfilePicture onClose={() => setShowProfileUpload(false)} onUploadSuccess={handleProfileUploadSuccess} />
  )}
  {showImageModal && (
    <div
      className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
      onClick={() => setShowImageModal(false)}
    >
      <div className="relative">
        <img
          src={modalImage}
          alt="Preview"
          className="max-h-[80vh] max-w-[80vw] object-contain rounded-lg"
        />
        <button
          onClick={() => setShowImageModal(false)}
          className="absolute top-2 right-2 text-white text-3xl"
        >
          ✕
        </button>
      </div>
    </div>
  )}
</div>
  </>
  );
};

const InputField = ({
  name,
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
  required = false,
  disabled = false,
}) => (
  <div className="mb-4">
    <label
      htmlFor={name}
      className="block text-sm font-medium text-gray-700 mb-1"
    >
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      required={required}
      disabled={disabled}
      className="border border-gray-300 rounded-md w-full px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100"
    />
  </div>
);

const DisplayField = ({ label, value }) => (
  <div>
    <label className="text-sm font-medium text-gray-500">{label}</label>
    <p className="text-gray-800 text-sm mt-1">{value}</p>
  </div>
);

